import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Coffee, Beer, Sparkles, MessageCircleHeart } from "lucide-react";
import { ServicesGrid } from "../components/sections/ServicesGrid";
import { CtaBanner } from "../components/sections/CtaBanner";
import { PROCESS_STEPS } from "../lib/constants";

export default function ServicesPage() {
  return (
    <div data-testid="page-services">
      {/* HEADER */}
      <section className="relative pt-20 pb-20 md:pt-28 md:pb-24 overflow-hidden border-b border-zinc-800/70">
        <div className="rm-glow-top-right" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <div className="rm-overline mb-5">Services — Lead Generation & Consulting</div>
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter text-white max-w-4xl leading-[0.95]">
            One agency. <span className="text-violet-400">All Results</span>.
            <br />
            Zero excuses.
          </h1>
          <p className="mt-6 text-lg text-zinc-400 max-w-2xl leading-relaxed">
            We run lead-generation ads in-house across every major platform,
            and we sit down with founders directly when you need a clear plan
            more than you need an agency.
          </p>
          <div className="mt-8 flex items-center gap-4 flex-wrap">
            <Link
              to="/contact"
              data-testid="services-header-cta"
              className="rm-btn-primary"
            >
              Book a free first call <ArrowUpRight className="h-4 w-4" />
            </Link>
            <a
              href="#services-grid"
              data-testid="services-header-scroll"
              className="rm-btn-secondary"
            >
              See the menu
            </a>
          </div>
        </div>
      </section>

      {/* GRID — Media Buying */}
      <section
        id="services-grid"
        data-testid="services-full-grid"
        className="relative py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="rm-overline mb-4">Section 01 — Lead Generation</div>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
                Online marketing that turns attention into customers, and
                customers into revenue.
              </h2>
              <p className="mt-4 text-zinc-400 leading-relaxed">
                We run your ads across the platforms people actually spend
                their time on — writing the copy, building the creative,
                picking the audiences, and tuning every week so your spend
                keeps turning into real customers and real revenue.
              </p>
            </div>
            <div className="rm-divider-violet self-start md:self-auto" />
          </div>
          <ServicesGrid detailed />
        </div>
      </section>

      {/* MARKETING CONSULTING — violet-forward with warm touches */}
      <section
        data-testid="consulting-spotlight"
        className="relative py-20 md:py-28 border-t border-zinc-800/70 overflow-hidden"
      >
        {/* Violet ambient glow (matches the rest of the site) */}
        <div className="rm-glow-top-right" aria-hidden="true" />
        <div className="rm-glow-bottom-left" aria-hidden="true" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <div className="rm-overline mb-4">
            Section 02 — Business Consulting
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight mb-12 max-w-3xl">
            Strategy you can actually{" "}
            <span className="text-violet-400">act on</span>.
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            {/* Left — pitch */}
            <div className="lg:col-span-7">
              <div className="rm-overline mb-5 flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-violet-400" />
                low pressure · high signal
              </div>
              <h3 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter text-white leading-[1.05]">
                Big ideas over{" "}
                <span style={{ color: "#FBBF24" }}>coffee</span> ☕.
                <br />
                Bigger plans over{" "}
                <span style={{ color: "#F59E0B" }}>beer</span> 🍺.
              </h3>

              <div className="mt-7 space-y-5 text-zinc-300 leading-relaxed text-lg max-w-2xl">
                <p>
                  Sometimes you don't need an agency — you need a real
                  conversation with someone who's been in the trenches. That's
                  what consulting with me looks like. 🧠💬
                </p>
                <p>
                  We sit down, talk through what's actually happening in your
                  business, and I share the playbooks, case studies, and
                  hard-earned lessons that move the needle. We borrow what's
                  worked for similar brands, skip the stuff that won't, and
                  put your best foot forward so you hit the ground running.
                  🚀
                </p>
                <p className="text-zinc-400">
                  No decks. No 90-page audits. Just smart, honest advice from
                  someone who actually wants you to win — preferably with a
                  warm drink in hand. 🤝
                </p>
              </div>

              <div className="mt-9 flex flex-wrap items-center gap-3">
                <Link
                  to="/contact"
                  data-testid="consulting-cta-primary"
                  className="rm-btn-primary"
                >
                  Let's grab one ☕ <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="consulting-cta-secondary"
                  className="rm-btn-secondary"
                >
                  Book a virtual chat
                </Link>
              </div>
            </div>

            {/* Right — pick your poison cards */}
            <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
              {/* Coffee card — violet with amber accent */}
              <div
                data-testid="consulting-card-coffee"
                className="rm-card rm-service-card p-7 relative overflow-hidden"
                style={{
                  "--brand": "#A78BFA",
                  "--brand-glow": "rgba(167,139,250,0.45)",
                }}
              >
                <div className="flex items-start justify-between mb-5">
                  <div
                    className="h-12 w-12 flex items-center justify-center border border-zinc-800 bg-zinc-900/60 rm-service-icon-tile transition-all duration-300"
                    style={{ color: "#FBBF24" }}
                  >
                    <Coffee className="h-5 w-5" />
                  </div>
                  <span className="font-mono text-xs text-zinc-500">
                    Option A
                  </span>
                </div>
                <div className="rm-overline mb-2 text-violet-400">
                  ☕ Coffee Chat
                </div>
                <h3 className="font-display text-2xl font-bold tracking-tight text-white mb-2">
                  60 min. Zero pressure.
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  Local in Victoria? Let's meet up. We'll talk channels,
                  funnels, creative, and what's keeping you up at night.
                </p>
                <ul className="mt-5 space-y-2 text-sm text-zinc-300">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0 text-violet-400" />
                    Live audit of your current ads
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0 text-violet-400" />
                    3 quick wins you can ship this week
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0 text-violet-400" />
                    Coffee's on me ✨
                  </li>
                </ul>
              </div>

              {/* Beer card — deeper violet with amber accent */}
              <div
                data-testid="consulting-card-beer"
                className="rm-card rm-service-card p-7 relative overflow-hidden"
                style={{
                  "--brand": "#8B5CF6",
                  "--brand-glow": "rgba(139,92,246,0.45)",
                }}
              >
                <div className="flex items-start justify-between mb-5">
                  <div
                    className="h-12 w-12 flex items-center justify-center border border-zinc-800 bg-zinc-900/60 rm-service-icon-tile transition-all duration-300"
                    style={{ color: "#F59E0B" }}
                  >
                    <Beer className="h-5 w-5" />
                  </div>
                  <span className="font-mono text-xs text-zinc-500">
                    Option B
                  </span>
                </div>
                <div className="rm-overline mb-2 text-violet-300">
                  🍺 Happy Hour Drinks
                </div>
                <h3 className="font-display text-2xl font-bold tracking-tight text-white mb-2">
                  Big-picture, late afternoon.
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  Bigger questions, longer conversation. We map out a 90-day
                  plan and the case studies we're stealing inspiration from.
                </p>
                <ul className="mt-5 space-y-2 text-sm text-zinc-300">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0 text-violet-400" />
                    Channel mix & growth roadmap
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0 text-violet-400" />
                    Case studies from similar brands
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0 text-violet-400" />
                    First round's on me 🍻
                  </li>
                </ul>
              </div>

              {/* Tiny note */}
              <div
                className="hidden sm:flex lg:flex items-center gap-3 px-5 py-3 border border-zinc-800/80 bg-zinc-900/40 text-xs font-mono text-zinc-400"
                data-testid="consulting-card-note"
              >
                <MessageCircleHeart className="h-4 w-4 text-violet-400" />
                Not in Victoria? We can absolutely Zoom — same energy, less
                foam.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section
        data-testid="process-section"
        className="relative py-20 md:py-28 border-t border-zinc-800/70"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-16 max-w-2xl">
            <div className="rm-overline mb-4">How we work</div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-white">
              A simple, repeatable way of working.
            </h2>
            <p className="mt-5 text-zinc-400 leading-relaxed">
              Every project follows the same four steps. Predictable process,
              real results.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROCESS_STEPS.map((p) => (
              <div
                key={p.step}
                data-testid={`process-step-${p.step}`}
                className="rm-card p-8 flex flex-col"
              >
                <div className="font-mono text-xs text-violet-400 mb-4">
                  STEP / {p.step}
                </div>
                <h3 className="font-display text-2xl font-bold tracking-tight text-white mb-3">
                  {p.title}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  {p.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING NOTE */}
      <section
        data-testid="pricing-note"
        className="relative py-20 md:py-24 border-t border-zinc-800/70"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6">
            <div className="rm-overline mb-4">Pricing</div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-white">
              Built around your business, not a pricing grid.
            </h2>
          </div>
          <div className="lg:col-span-6">
            <p className="text-zinc-400 leading-relaxed text-lg">
              Every project starts with a free first call. We learn your goals,
              look at what you already have, and only then suggest a plan and
              price that actually makes sense for your business.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Monthly retainer — we run your ads month after month",
                "Project-based consulting — quick reviews, launches, or planning",
                "Part-time marketing leader — senior help without a full-time hire",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-zinc-300">
                  <Check className="h-5 w-5 text-violet-400 mt-0.5 flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/contact"
              data-testid="pricing-cta"
              className="rm-btn-primary mt-8"
            >
              Start with a free call <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
